import { useEffect, type RefObject } from 'react'

/**
 * Keep Tab inside a modal dialog.
 *
 * Both confirmation dialogs declare aria-modal="true", which PROMISES a
 * screen-reader user that the rest of the page is inert — but nothing
 * actually stopped Tab from wandering into the control bar behind the scrim.
 * This closes that gap the cheap way: Tab past the last focusable wraps to
 * the first, Shift+Tab past the first wraps to the last. Escape handling and
 * initial focus stay with each dialog; this hook does exactly one job.
 *
 * Deliberately NOT used on the chat/roster/background sheets: those are
 * aria-modal="false" non-modal panels where leaving by keyboard is correct.
 */
const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

export function useFocusTrap(containerRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const container = containerRef.current
      if (!container) return
      const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement

      // Focus escaped (or never entered): pull it back rather than trap air.
      if (!active || !container.contains(active)) {
        event.preventDefault()
        first.focus()
        return
      }
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [containerRef])
}
